import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Bell, BellSlash, BellRinging, Check } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

const isSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const PushBell = () => {
  const [supported] = useState(() => isSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );

  // On mount, register SW and check existing subscription
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
        const ready = await navigator.serviceWorker.ready;
        const existing = await (ready.pushManager || reg.pushManager).getSubscription();
        if (!cancelled) setSubscribed(!!existing);
      } catch {
        // ignore — SW unsupported / blocked
      }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const subscribe = useCallback(async () => {
    setBusy(true);
    // Optimistic flip — UI feels instant; we revert if subscription fails.
    setSubscribed(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setSubscribed(false);
        setBusy(false);
        return;
      }

      const { data } = await axios.get(`${API}/push/public-key`);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const json = sub.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
    } catch (e) {
      console.error("Push subscribe failed:", e);
      setSubscribed(false);
    } finally { setBusy(false); }
  }, []);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try {
          await axios.post(`${API}/push/unsubscribe`, { endpoint });
        } catch { /* ignore — local unsub is enough */ }
      }
      setSubscribed(false);
    } catch (e) {
      console.error("Push unsubscribe failed:", e);
    } finally { setBusy(false); }
  }, []);

  if (!supported) return null;

  const denied = permission === "denied";
  const onClick = subscribed ? unsubscribe : subscribe;
  const label = denied
    ? "Notifications blocked — enable in browser settings"
    : subscribed ? "Disable push alerts" : "Enable push alerts for new horses";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || denied}
      data-testid={subscribed ? "push-bell-on" : "push-bell-off"}
      aria-label={label}
      title={label}
      style={{
        width: "100%",
        background: "#fff", border: "1px solid #e5e5e4",
        cursor: denied ? "not-allowed" : "pointer",
        padding: "12px 16px", borderRadius: 12,
        display: "flex", alignItems: "center", gap: 12,
        textAlign: "left",
        color: "#323233",
        opacity: denied ? 0.4 : (busy ? 0.6 : 1),
        transition: "opacity 0.2s ease, border-color 0.2s ease, background 0.2s ease",
        fontFamily: "inherit",
      }}
      onMouseEnter={(e) => { if (!denied && !busy) e.currentTarget.style.borderColor = "#323233"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e5e4"; }}
    >
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32, borderRadius: 999,
        background: subscribed ? "#ecfdf5" : "#f4f4f3",
        color: subscribed ? "#0a7d3a" : "#666",
        flexShrink: 0,
      }}>
        {subscribed ? <Bell size={16} weight="fill" /> : <BellSlash size={16} weight="bold" />}
      </span>
      <span style={{
        flex: 1, minWidth: 0, fontSize: 13, color: "#323233", lineHeight: 1.4,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {subscribed
          ? "Alerts now activated."
          : "Tap to receive real-time FTP alerts."}
      </span>
    </button>
  );
};


// ============== Push CTA Card ==============
// A prominent inline card for the main feed. Encourages subscription,
// shows confirmation when subscribed, and offers a one-tap toggle off.

export const PushSubscriptionCard = () => {
  const [supported] = useState(() => isSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
        const ready = await navigator.serviceWorker.ready;
        const existing = await (ready.pushManager || reg.pushManager).getSubscription();
        if (!cancelled) setSubscribed(!!existing);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const subscribe = useCallback(async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") { setBusy(false); return; }
      const { data } = await axios.get(`${API}/push/public-key`);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const json = sub.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setSubscribed(true);
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  }, []);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try { await axios.post(`${API}/push/unsubscribe`, { endpoint }); } catch { /* ignore */ }
      }
      setSubscribed(false);
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  }, []);

  if (!supported) return null;

  const denied = permission === "denied";

  if (subscribed) {
    return (
      <div data-testid="push-cta-subscribed" style={{
        margin: "16px",
        padding: "14px 16px",
        background: "linear-gradient(135deg, #064e3b 0%, #0a7d3a 100%)",
        borderRadius: 12,
        color: "#fff",
        display: "flex", alignItems: "center", gap: 12,
        boxShadow: "0 6px 16px rgba(10,125,58,0.25)",
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 18,
          background: "rgba(255,255,255,0.18)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <Check size={20} weight="bold" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
            Alerts on
          </div>
          <div style={{ fontSize: 11, opacity: 0.85, lineHeight: 1.4 }}>
            You'll get a push the moment a new horse hits the dashboard (9am–11pm AEST).
          </div>
        </div>
        <button
          type="button"
          onClick={unsubscribe}
          disabled={busy}
          data-testid="push-cta-unsubscribe"
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "#fff", padding: "6px 10px", borderRadius: 6,
            fontSize: 10, fontWeight: 700, cursor: "pointer",
            textTransform: "uppercase", letterSpacing: "0.05em",
            opacity: busy ? 0.6 : 1,
          }}
        >Turn off</button>
      </div>
    );
  }

  return (
    <div data-testid="push-cta-card" style={{
      margin: "16px",
      padding: "16px 18px",
      background: "linear-gradient(135deg, #1e293b 0%, #323233 100%)",
      borderRadius: 12,
      color: "#fff",
      boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 21,
          background: "rgba(251, 191, 36, 0.18)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <BellRinging size={22} weight="fill" color="#fbbf24" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            Never miss a Best Bet
          </div>
          <div style={{ fontSize: 12, opacity: 0.78, lineHeight: 1.5, marginBottom: 12 }}>
            Get a push alert the second a new horse lands on the dashboard.
            Works in your browser or on your home screen — no download required.
          </div>
          <button
            type="button"
            onClick={subscribe}
            disabled={busy || denied}
            data-testid="push-cta-subscribe"
            style={{
              background: denied ? "rgba(255,255,255,0.1)" : "#fbbf24",
              color: denied ? "#999" : "#1e293b",
              border: "none", padding: "9px 18px", borderRadius: 8,
              fontWeight: 700, fontSize: 12,
              cursor: denied || busy ? "not-allowed" : "pointer",
              textTransform: "uppercase", letterSpacing: "0.05em",
              display: "inline-flex", alignItems: "center", gap: 6,
              opacity: busy ? 0.6 : 1,
            }}
          >
            <Bell size={14} weight="fill" />
            {denied ? "Blocked in browser settings" : busy ? "Enabling…" : "Enable Alerts"}
          </button>
        </div>
      </div>
    </div>
  );
};
